// components/scans/Scans.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Chip,
  Autocomplete,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Inventory as InventoryIcon,
  FilterList,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  TableChart as TableChartIcon,
  ViewColumn as ViewColumnIcon,
  Description as DescriptionIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { scanAPI, stockAPI, scanCategoryAPI } from '../../services/api';
import SearchBar from '../../components/common/SearchBar';
import NoContent from '../../components/common/NoContent';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import NoPrivilege from '../../components/common/NoPrivilege';
import ScansTable from './scansTable.js';
import AddScanDialog from './addScan.js';

// Export format options
const EXPORT_FORMATS = {
  simple: {
    key: 'simple',
    labelKey: 'scans.export.simple',
    defaultLabel: 'Simple Export',
    descriptionKey: 'scans.export.simpleDesc',
    defaultDescription: 'All branch prices in one column',
    icon: <DescriptionIcon fontSize="small" />
  },
  detailed: {
    key: 'detailed',
    labelKey: 'scans.export.detailed',
    defaultLabel: 'Detailed Export',
    descriptionKey: 'scans.export.detailedDesc',
    defaultDescription: 'Separate sheet with branch pricing details & profit margins',
    icon: <TableChartIcon fontSize="small" />
  },
  dynamic: {
    key: 'dynamic',
    labelKey: 'scans.export.byBranch',
    defaultLabel: 'Export by Branch',
    descriptionKey: 'scans.export.byBranchDesc',
    defaultDescription: 'Each branch as a separate column',
    icon: <ViewColumnIcon fontSize="small" />
  }
};

const Scans = () => {
  const { t } = useTranslation();
  const { user, isSuperAdmin } = useAuth();

  // Filter options
  const SORT_BY_OPTIONS = [
    { value: 'name', label: t('common.name') },
    { value: 'price', label: t('common.price') },
    { value: 'createdAt', label: t('common.createdAt') },
  ];

  const SORT_ORDER_OPTIONS = [
    { value: 'asc', label: t('common.ascending') },
    { value: 'desc', label: t('common.descending') },
  ];

  // States
  const [scans, setScans] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogDataLoading, setDialogDataLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [privilegeError, setPrivilegeError] = useState(false);

  // Export States
  const [exporting, setExporting] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);

  // Consolidated filter state
  const [filters, setFilters] = useState({
    search: '',
    category: null,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  // Permissions
  const canAdd = isSuperAdmin || user?.privileges?.some(p => p.module === 'scans' && p.operations.includes('create'));
  const canEdit = isSuperAdmin || user?.privileges?.some(p => p.module === 'scans' && p.operations.includes('update'));
  const canDelete = isSuperAdmin || user?.privileges?.some(p => p.module === 'scans' && p.operations.includes('delete'));
  const canView = isSuperAdmin || user?.privileges?.some(p => p.module === 'scans' && p.operations.includes('view'));
  const canExport = canView;
  const isRadiologist = user?.userType === 'radiologist';
  const isRepresentative = user?.userType === 'representative';


  const fetchScans = useCallback(async () => {
    setLoading(true);
    setPrivilegeError(false);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      // CHANGED: Use 'name' instead of 'search' for scan name filtering
      if (filters.search) {
        params.name = filters.search;
      }

      if (filters.category) {
        params.category = filters.category._id;
      }

      const response = await scanAPI.getAll(params);
      setScans(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (err) {
      const backendMessage = err.response?.data?.message;
      if (backendMessage && backendMessage.toLowerCase().includes('insufficient privileges')) {
        setPrivilegeError(true);
      } else {
        toast.error(backendMessage || t('scans.fetchError'));
      }
      setScans([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, t]);

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await scanCategoryAPI.getActive();
        const categoriesData = response.data?.data || response.data || [];
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error fetching scan categories:', err);
        toast.error('Failed to fetch scan categories');
      }
    };
    fetchCategories();
  }, []);

  // --- Filter Handlers ---
  const handleFilterChange = (key, value) => {
    setPage(0);
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setPage(0);
    setFilters({
      search: '',
      category: null,
      sortBy: 'name',
      sortOrder: 'asc',
    });
  };

  // Check if any filter is active
  const hasActiveFilters = filters.search || filters.category;

  // --- Export Menu Handlers ---
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  // --- Export Handler ---
  const handleExport = async (format = 'simple') => {
    handleExportMenuClose();
    setExporting(true);

    try {
      // Build export params from current filters
      const params = {
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };

      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category._id;

      let response;
      let filenamePrefix = 'scans';

      // Call appropriate API based on format
      switch (format) {
        case 'detailed':
          response = await scanAPI.exportDetailed(params);
          filenamePrefix = 'scans_detailed';
          break;
        case 'dynamic':
          response = await scanAPI.exportByBranch(params);
          filenamePrefix = 'scans_by_branch';
          break;
        case 'simple':
        default:
          params.exportFormat = 'simple';
          response = await scanAPI.export(params);
          filenamePrefix = 'scans';
          break;
      }

      // Create blob and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with date
      const date = new Date().toISOString().split('T')[0];
      link.download = `${filenamePrefix}_${date}.xlsx`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success(t('scans.exportSuccess', 'Scans exported successfully'));
    } catch (err) {
      console.error('Export error:', err);
      toast.error(
        err.response?.data?.message ||
        t('scans.exportError', 'Failed to export scans')
      );
    } finally {
      setExporting(false);
    }
  };

  // --- Action Handlers ---
  const restoreScan = async (scanId) => {
    try {
      await scanAPI.restore(scanId.id);
      toast.success(t('scans.restoreSuccess', 'Scan restored successfully'));
      fetchScans();
    } catch (err) {
      console.error('Restore error:', err);
      toast.error(t('scans.restoreError', 'Failed to restore scan'));
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = async (scan = null) => {
    setSelectedScan(scan);
    setOpenDialog(true);
    if (stockItems.length === 0) {
      setDialogDataLoading(true);
      try {
        const response = await stockAPI.getAll({ limit: 1000 });
        const items = response.data?.data;
        setStockItems(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error('fetchStockItems error:', err);
        toast.error(`Failed to fetch stock items: ${err.message}`);
      } finally {
        setDialogDataLoading(false);
      }
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedScan(null);
  };

  const handleScanSaved = () => {
    handleCloseDialog();
    fetchScans();
  };

  const handleDelete = async () => {
    if (!selectedScan) return;
    try {
      await scanAPI.delete(selectedScan._id);
      toast.success(t('scans.deleteSuccess', 'Scan deleted successfully'));
      fetchScans();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error(t('scans.deleteError', 'Failed to delete scan'));
    } finally {
      setOpenConfirm(false);
      setSelectedScan(null);
    }
  };

  // --- Permission Check ---
  if (!canView || privilegeError) {
    return <NoPrivilege message={t('scans.noPrivilegeError') || 'You do not have permission to view scans.'} />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h4" component="h1">
            {t('scans.title')}
          </Typography>
          {!loading && (
            <Chip
              label={`${total} ${t('common.total', 'total')}`}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {/* Export Button with Dropdown */}
          {canExport && (
            <>
              <Button
                variant="outlined"
                startIcon={exporting ? <CircularProgress size={18} /> : <FileDownloadIcon />}
                endIcon={<ArrowDownIcon />}
                onClick={handleExportMenuOpen}
                disabled={exporting || loading}
                aria-controls={exportMenuOpen ? 'export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={exportMenuOpen ? 'true' : undefined}
              >
                {exporting
                  ? t('common.exporting', 'Exporting...')
                  : t('common.export', 'Export')
                }
              </Button>
              <Menu
                id="export-menu"
                anchorEl={exportMenuAnchor}
                open={exportMenuOpen}
                onClose={handleExportMenuClose}
                MenuListProps={{
                  'aria-labelledby': 'export-button',
                }}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
              >
                {/* Simple Export */}
                <MenuItem onClick={() => handleExport('simple')}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.simple.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.simple.labelKey, EXPORT_FORMATS.simple.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.simple.descriptionKey, EXPORT_FORMATS.simple.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Detailed Export */}
                <MenuItem onClick={() => handleExport('detailed')}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.detailed.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.detailed.labelKey, EXPORT_FORMATS.detailed.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.detailed.descriptionKey, EXPORT_FORMATS.detailed.defaultDescription)}
                  />
                </MenuItem>

                <Divider />

                {/* Export by Branch */}
                <MenuItem onClick={() => handleExport('dynamic')}>
                  <ListItemIcon>
                    {EXPORT_FORMATS.dynamic.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(EXPORT_FORMATS.dynamic.labelKey, EXPORT_FORMATS.dynamic.defaultLabel)}
                    secondary={t(EXPORT_FORMATS.dynamic.descriptionKey, EXPORT_FORMATS.dynamic.defaultDescription)}
                  />
                </MenuItem>
              </Menu>
            </>
          )}
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              {t('scans.addScanButton', 'Add Scan')}
            </Button>
          )}
        </Stack>
      </Box>

      {/* Filters Paper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={2} mb={2}>
          <FilterList />
          <Typography variant="h6">{t('filters', 'Filters')}</Typography>
          {/* Filter indicator for export */}
          {hasActiveFilters && (
            <Chip
              label={t('common.filtersApplied', 'Filters will be applied to export')}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Stack>

        {/* Search and Sort Row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <SearchBar
            onSearch={(query) => handleFilterChange('search', query)}
            initialValue={filters.search}
            placeholder={t('scans.searchPlaceholder')}
            sx={{ flexGrow: 1, minWidth: '250px' }}
          />

          {/* Category Filter */}
          <Autocomplete
            options={categories}
            getOptionLabel={(option) => option.name || ''}
            value={filters.category}
            onChange={(event, newValue) => handleFilterChange('category', newValue)}
            renderInput={(params) => (
              <TextField {...params} label={t('scans.category', 'Category')} size="small" />
            )}
            sx={{ minWidth: 180 }}
            isOptionEqualToValue={(option, value) => option._id === value?._id}
          />

          <Autocomplete
            options={SORT_BY_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_BY_OPTIONS.find(opt => opt.value === filters.sortBy)}
            onChange={(event, newValue) => handleFilterChange('sortBy', newValue?.value || 'name')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.sortBy', 'Sort By')} size="small" />
            )}
            sx={{ minWidth: 180 }}
            disableClearable
          />

          <Autocomplete
            options={SORT_ORDER_OPTIONS}
            getOptionLabel={(option) => option.label}
            value={SORT_ORDER_OPTIONS.find(opt => opt.value === filters.sortOrder)}
            onChange={(event, newValue) => handleFilterChange('sortOrder', newValue?.value || 'asc')}
            renderInput={(params) => (
              <TextField {...params} label={t('common.order', 'Order')} size="small" />
            )}
            sx={{ minWidth: 150 }}
            disableClearable
          />

          {/* Clear Filters */}
          {hasActiveFilters && (
            <Button
              variant="text"
              color="inherit"
              onClick={handleClearFilters}
              startIcon={<ClearIcon />}
              size="small"
            >
              {t('common.clearFilters', 'Clear')}
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {filters.search && (
            <Chip
              label={`${t('common.search', 'Search')}: ${filters.search}`}
              size="small"
              onDelete={() => handleFilterChange('search', '')}
            />
          )}
          {filters.category && (
            <Chip
              label={`${t('scans.category', 'Category')}: ${filters.category.name}`}
              size="small"
              color="primary"
              onDelete={() => handleFilterChange('category', null)}
            />
          )}
        </Box>
      )}

      {/* Results Info */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('common.showing', 'Showing')} {total === 0 ? 0 : Math.min(page * rowsPerPage + 1, total)}-{Math.min((page + 1) * rowsPerPage, total)} {t('common.of', 'of')} {total} {t('common.items', 'items')}
      </Typography>

      {/* Table / No Content */}
      {scans.length === 0 && !loading ? (
        <Paper sx={{ p: 4 }}>
          <NoContent
            icon={<InventoryIcon />}
            title={t('scans.noScansTitle')}
            description={
              filters.search
                ? t('scans.noSearchResults', 'No scans match your search')
                : filters.category
                  ? t('scans.noCategoryResults', 'No scans found in this category')
                  : t('scans.noScansDescription')
            }
            action={
              canAdd && !hasActiveFilters && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  {t('scans.addScanButton', 'Add Scan')}
                </Button>
              )
            }
          />
        </Paper>
      ) : (
        <ScansTable
          scans={scans}
          canEdit={canEdit}
          canDelete={canDelete}
          isRadiologist={isRadiologist}
          isRepresentative={isRepresentative}
          onEdit={handleOpenDialog}
          restoreScan={restoreScan}
          onDelete={(scan) => {
            setSelectedScan(scan);
            setOpenConfirm(true);
          }}
          page={page}
          rowsPerPage={rowsPerPage}
          total={total}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          loading={loading}
        />
      )}

      {/* Dialogs */}
      {openDialog && (
        <AddScanDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onScanSaved={handleScanSaved}
          selectedScan={selectedScan}
          stockItems={stockItems}
          categories={categories}
          stockLoading={dialogDataLoading}
          isRadiologist={isRadiologist}
        />
      )}

      <ConfirmDialog
        open={openConfirm}
        title={t('scans.deleteScanTitle')}
        message={t('scans.deleteScanContent')}
        onConfirm={handleDelete}
        onClose={() => {
          setOpenConfirm(false);
          setSelectedScan(null);
        }}
      />
    </Box>
  );
};

export default Scans;